import { ButtonPrimary, ButtonStatus, ButtonTertiary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import classNames from "classnames"
import { CheckboxInput } from "@nova/component-checkbox-input"
import { useCallback, useEffect, useState, type JSX } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectSelectedLayers } from "panels/content/side-panel/slice.new"
import { selectFireById } from "features/entity-caching/fires/selectors"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useExportLayers } from "./useExportLayers"
import { Category, DownloadOption, DownloadType } from "./types"
import { DownloadableLayerTypes, rootCategories } from "./module"
import { useAsyncCallback } from "@nova/use-async-callback"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { IntegrationExportEntity, SidePanelLayer, SidePanelLayerType } from "@nova/constants"
import { hideGlobalModal } from "runlevel/actions"
import { useIntegrationExportOptions } from "./useIntegrationExportOptions"

const integrationExportEntityByLayerType: Partial<
    Record<SidePanelLayerType, IntegrationExportEntity>
> = {
    [SidePanelLayerType.Icon]: IntegrationExportEntity.Icon,
    [SidePanelLayerType.Line]: IntegrationExportEntity.Line,
    [SidePanelLayerType.FirePerimeter]: IntegrationExportEntity.Perimeter,
}

export const ExportLayersModal = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const projectId = useAppSelector(selectActivatedFireId)
    const project = useAppSelector(selectFireById(projectId))!
    const projectName = project.name
    const selectedLayers = useAppSelector(selectSelectedLayers)

    const { handleDownloadClick: clickDownload } = useExportLayers()
    const integrationEntities = selectedLayers.flatMap((layer) => {
        const type = layer.type && integrationExportEntityByLayerType[layer.type]
        return type ? [{ id: layer.id, type }] : []
    })
    const integrationOptions = useIntegrationExportOptions(integrationEntities)

    const [categories, setCategories] = useState<Category[]>([])
    const [selectedFileTypes, setSelectedFileTypes] = useState<Set<DownloadType>>(new Set())
    const [selectionIsContaminated, setSelectionIsContaminated] = useState(false)
    const [integrationsCollapsed, setIntegrationsCollapsed] = useState(false)
    const [selectedIntegrations, setSelectedIntegrations] = useState<Set<string>>(new Set())

    const toggleIntegration = (name: string) =>
        setSelectedIntegrations((prev) => {
            const next = new Set(prev)
            if (!next.delete(name)) next.add(name)
            return next
        })

    const { loading: isLoading, handleDownloadClick } = useAsyncCallback(
        {
            handleDownloadClick: async () => {
                const sends = integrationOptions.filter(({ name }) =>
                    selectedIntegrations.has(name)
                )
                const [download, ...results] = await Promise.allSettled([
                    selectedFileTypes.size > 0 &&
                        // prototodo: make this find all child layers of `selectedLayers` when a selectedLayer is a group.
                        clickDownload(
                            projectName,
                            selectedLayers.filter(
                                (layerOrGroup) => layerOrGroup.type !== SidePanelLayerType.Group
                            ) as SidePanelLayer[],
                            selectedFileTypes
                        ),
                    ...sends.map(({ send }) => send()),
                ])
                const list = new Intl.ListFormat("en", { type: "conjunction" })
                const sent = list.format(
                    sends
                        .filter((_, i) => results[i].status === "fulfilled")
                        .map(({ name }) => name)
                )
                const unsent = list.format(
                    sends.filter((_, i) => results[i].status === "rejected").map(({ name }) => name)
                )
                const downloadFailed = download.status === "rejected"
                const count = integrationEntities.length
                const message = [
                    sent && `Sent ${count} shape${count === 1 ? "" : "s"} to ${sent}.`,
                    unsent && `Couldn't send to ${unsent}.`,
                    downloadFailed && "Download failed.",
                ]
                    .filter(Boolean)
                    .join(" ")
                if (!message) return
                const hasFailure = !!unsent || downloadFailed
                dispatch(
                    setGlobalNotificationContext({
                        message,
                        status: hasFailure
                            ? GlobalNotificationStatus.error
                            : GlobalNotificationStatus.success,
                        timeoutInMs: hasFailure ? 6000 : 4000,
                    })
                )
            },
        },
        [
            projectName,
            selectedLayers,
            selectedFileTypes,
            integrationOptions,
            selectedIntegrations,
            integrationEntities.length,
        ]
    )

    useEffect(() => {
        const stagedCategories: Category[] = []
        const stagedCategoryKeys: Set<SidePanelLayerType> = new Set()

        selectedLayers.forEach((layer) => {
            if (layer.type && !stagedCategoryKeys.has(layer.type)) {
                // Some layerNames like "text", "color-finder" are excluded.
                // Intentional copy of rootCategories to prevent accidential writing to it, since it's an initial state.
                const categories = rootCategories()
                if (categories[layer.type as DownloadableLayerTypes]) {
                    stagedCategoryKeys.add(layer.type)
                    stagedCategories.push({ ...categories[layer.type as DownloadableLayerTypes] })
                } else {
                    // An unsupported layer type like video has been selected.
                    setSelectionIsContaminated(true)
                }
            }
        })

        setCategories(stagedCategories)
    }, [selectedLayers])

    const handleDropdownClick = useCallback(
        (category: Category) => {
            category.collapsed = !category.collapsed
            setCategories([...categories])
        },
        [categories]
    )

    const handleCheckboxClick = useCallback(
        (downloadOption: DownloadOption) => {
            const change = !downloadOption.selected
            downloadOption.selected = change
            setSelectedFileTypes((prev) => {
                if (change) {
                    prev.add(downloadOption.type)
                } else {
                    prev.delete(downloadOption.type)
                }
                return prev
            })
            setCategories([...categories])
        },
        [categories]
    )

    /**
     * Recursive function to populate dropdown list from categories tree.
     */
    const populateDownloadOptions = useCallback(
        (categories: Category[], indent = 0, id = "") => {
            const items = categories.reduce((result, category, i) => {
                // Add this category title with dropdown ability.
                result.push(
                    <div
                        key={id + category.name}
                        className={classNames("flex h-8 gap-1 items-center", {
                            "border-t border-[#404040]": indent === 0 && i > 0,
                        })}
                        style={{ marginLeft: indent * 20 }}
                        onClick={() => handleDropdownClick(category)}
                    >
                        <div>
                            <Icons.ChevronUp
                                className={classNames(
                                    "w-4 !m-0 !h-4 rounded hover:bg-[#424242] duration-150 ease-in-out",
                                    { "-rotate-90": category.collapsed }
                                )}
                            />
                        </div>
                        <h5 className="select-none">{t(category.name)}</h5>
                    </div>
                )

                if (!category.collapsed) {
                    // Recursively add sub-categories.
                    if (category.categories) {
                        result.push(
                            ...populateDownloadOptions(
                                category.categories,
                                indent + 1,
                                category.name + "-"
                            )
                        )
                    }
                    // Otherwise, add download options.
                    else if (category.downloadOptions) {
                        category.downloadOptions.forEach((downloadOption, i) => {
                            result.push(
                                <div
                                    key={id + category.name + i}
                                    className="flex h-8 gap-1 items-center justify-between"
                                    style={{ marginLeft: (indent + 2) * 20 }}
                                >
                                    <small className="text-nova-soft-white select-none">
                                        {t(downloadOption.name)}
                                    </small>
                                    <CheckboxInput
                                        id={id + category.name + i}
                                        value={!!downloadOption.selected}
                                        onChange={() => handleCheckboxClick(downloadOption)}
                                    />
                                </div>
                            )
                        })
                    }
                }

                return result
            }, [] as JSX.Element[])

            return items
        },
        [handleDropdownClick, handleCheckboxClick, t]
    )

    return (
        <HighPriorityNovaModalContainer
            title="Export Shapes"
            className="w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                    <ButtonPrimary
                        title="Export Shapes"
                        onClick={handleDownloadClick}
                        disabled={selectedFileTypes.size === 0 && selectedIntegrations.size === 0}
                        status={isLoading ? ButtonStatus.Loading : ButtonStatus.Normal}
                    />
                </div>
            }
            closeModal={() => dispatch(hideGlobalModal())}
        >
            <h5 className="text-nova-soft-white select-none mt-3">
                {t("Select one or more formats or integrations to export")}
            </h5>
            <ScrollableArea id="export-layers-modal" className="w-full pr-4 mt-4 mb-2">
                {populateDownloadOptions(categories)}
                {integrationOptions.length > 0 && (
                    <>
                        <div
                            className="flex h-8 gap-1 items-center border-t border-[#404040]"
                            onClick={() => setIntegrationsCollapsed(!integrationsCollapsed)}
                        >
                            <Icons.ChevronUp
                                className={classNames(
                                    "w-4 !m-0 !h-4 rounded hover:bg-[#424242] duration-150 ease-in-out",
                                    { "-rotate-90": integrationsCollapsed }
                                )}
                            />
                            <h5 className="select-none">{t("Integrations")}</h5>
                        </div>
                        {!integrationsCollapsed &&
                            integrationOptions.map(({ name }) => (
                                <div
                                    key={name}
                                    className="flex h-8 gap-1 items-center justify-between"
                                    style={{ marginLeft: 40 }}
                                >
                                    <small className="text-nova-soft-white select-none">
                                        {t(`Send to ${name}`)}
                                    </small>
                                    <CheckboxInput
                                        id={name}
                                        value={selectedIntegrations.has(name)}
                                        onChange={() => toggleIntegration(name)}
                                    />
                                </div>
                            ))}
                    </>
                )}
            </ScrollableArea>
            {selectionIsContaminated && (
                <h5 className="text-nova-soft-white text-xs text-left font-light select-none mt-6">
                    {t("*Shapes apply to Icon, Line, and Polygon layers")}
                </h5>
            )}
        </HighPriorityNovaModalContainer>
    )
}
